import {
  GOFO_BYOCA_ORIGIN_HUBS,
  GofoByocaOriginHub,
} from "@redotech/redo-fulfillment-carrier-common/gofo-hubs";
import { CarrierAccountType } from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { useTranslation } from "@redotech/redo-i18n";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import { RedoTextInput } from "../../../../arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "../../../../arbiter-components/links/redo-external-link";
import { RedoListItem } from "../../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";

export const GofoCarrierInput = groupInput(
  {
    username: input<string>({ validator: nonEmptyValidator }),
    password: input<string>({ validator: nonEmptyValidator }),
    originHub: input<GofoByocaOriginHub | undefined>(),
    replacesCarrierAccountId: input<string | undefined>(),
  },
  {
    // A reconnect may omit the hub; the server derives it from the replaced account.
    validator: ({ originHub, replacesCarrierAccountId }) =>
      replacesCarrierAccountId ? [] : nonEmptyValidator(originHub),
  },
);

export type GofoCarrierValue = InputSchema.Value<typeof GofoCarrierInput>;

export type GofoCarrierForm = InputSchema.Input<typeof GofoCarrierInput>;

export const gofoCarrierDefault = (): GofoCarrierValue => ({
  username: "",
  password: "",
  originHub: undefined,
  replacesCarrierAccountId: undefined,
});

const originHubOptions: RedoListItem<GofoByocaOriginHub>[] =
  GOFO_BYOCA_ORIGIN_HUBS.map((hub) => ({
    id: hub.code,
    type: "text",
    text: `${hub.code} - ${hub.label}`,
    value: hub.code,
  }));

interface CreateGofoCarrierFormProps {
  carrierAccountType: CarrierAccountType;
  carrierInput: GofoCarrierForm;
  direct?: boolean;
  hasEasypostGofoAccount?: boolean;
}

export const CreateGofoCarrierForm = memo(function CreateGofoCarrierForm({
  carrierAccountType,
  carrierInput,
  direct = false,
  hasEasypostGofoAccount = false,
}: CreateGofoCarrierFormProps) {
  const { t } = useTranslation();
  const reconnecting = !!carrierInput.inputs.replacesCarrierAccountId.value;
  let originHubDescription: string | undefined;
  if (direct && reconnecting) {
    originHubDescription = t(
      "Leave blank to keep the hub this account already ships from.",
    );
  } else if (direct && hasEasypostGofoAccount) {
    // Each hint names its row's gofoDirectConnectButtonText.
    switch (carrierAccountType) {
      case CarrierAccountType.MERCHANT:
        originHubDescription = t(
          "You already have GOFO connected. Use Reconnect to switch it.",
        );
        break;
      case CarrierAccountType.REDO_CHILD:
        originHubDescription = t(
          "This team already has GOFO connected. Use Connect GOFO directly to switch it.",
        );
        break;
      default:
        assertNever(carrierAccountType);
    }
  }

  return (
    <>
      <Flex align="flex-start" flexDirection="column" gap="xs" mb="lg">
        <Text fontSize="xs" textColor="secondary">
          {direct
            ? t("Enter the API username and password GOFO gave you.")
            : t(
                "Use shipper credentials issued by GOFO and ask GOFO to enable EasyPost label purchases. Negotiated rates are available in production mode. Select the supported origin hub closest to your shipment origin address.",
              )}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          {t(
            "GOFO Parcel ships within the US, up to 25 lb and 30 inches in length. Returns, hazardous materials, signatures, and manifests are not supported. Arrange pickups directly with GOFO.",
          )}
        </Text>
        {!direct && (
          <RedoExternalLink
            text={t("GOFO account setup and supported services")}
            url="https://docs.easypost.com/carriers/gofo-byoca-guide"
          />
        )}
      </Flex>
      <RedoTextInput
        label={t("Username")}
        placeholder=""
        setValue={carrierInput.inputs.username.setValue}
        value={carrierInput.inputs.username.value}
      />
      <RedoTextInput
        inputTypeHint="password"
        label={t("Password")}
        placeholder=""
        setValue={carrierInput.inputs.password.setValue}
        value={carrierInput.inputs.password.value}
      />
      <RedoSingleSelectDropdownInput
        description={originHubDescription}
        label={t("Origin hub")}
        options={originHubOptions}
        optionSelected={({
          item,
        }: {
          item: RedoListItem<GofoByocaOriginHub>;
        }) => {
          carrierInput.inputs.originHub.setValue(item.value);
        }}
        selectedItem={originHubOptions.find(
          (option) => option.value === carrierInput.inputs.originHub.value,
        )}
      />
    </>
  );
});
